import { action$, extractPayload, ofType, withNamespace } from '@ardoq/rxbeach';
import { NodeModel } from '../models/types';
import { tap, withLatestFrom } from 'rxjs';
import { GetAdditionalRoutineArgs, NavigatorConfiguration } from '../types';
import { contextMenuClicked } from '../actions/navigatorActions';
import { targetManager } from '../targetManager';
import {
  COMPONENT_ITEM_CLASS_NAME,
  WORKSPACE_ITEM_CLASS_NAME,
  SCENARIO_ITEM_CLASS_NAME,
} from '../consts';
import { NavigatorViewInterface } from '../NavigatorViewInterface';
import { identity } from 'lodash';

export const getNavigatorContextMenuRoutine = ({
  navigator$,
  navigatorConfiguration,
}: GetAdditionalRoutineArgs) =>
  action$.pipe(
    navigatorConfiguration.actionNamespace
      ? withNamespace(navigatorConfiguration.actionNamespace)
      : identity,
    ofType(contextMenuClicked),
    extractPayload(),
    withLatestFrom(navigator$),
    tap(([event, { tree, isViewpointMode, selection }]) => {
      const { eventHandlers } = navigatorConfiguration;
      event.preventDefault();
      const { target, className } = targetManager.getTarget(
        event,
        'contextmenu'
      );
      const node = target ? tree.getNodeByDomNode(target) : null;
      if (!node) {
        return;
      }

      if (className === COMPONENT_ITEM_CLASS_NAME) {
        return createContextMenuForComponents(
          event,
          node,
          selection.filter(tree.navigatorViewInterface.isComponent),
          isViewpointMode,
          eventHandlers,
          tree.getNextSelectionCandidate,
          tree.navigatorViewInterface
        );
      }

      if (className === WORKSPACE_ITEM_CLASS_NAME) {
        return createContextMenuForWorkspace(
          event,
          node,
          isViewpointMode,
          tree.navigatorViewInterface.getActiveScenarioId(),
          eventHandlers
        );
      }
      if (className === SCENARIO_ITEM_CLASS_NAME) {
        return createContextMenuForScenario(
          event,
          tree.navigatorViewInterface.getActiveScenarioId(),
          eventHandlers
        );
      }
    })
  );

const createContextMenuForComponents = (
  event: MouseEvent,
  node: NodeModel,
  selectedComponentIds: string[],
  isViewpointMode: boolean,
  eventHandlers: NavigatorConfiguration['eventHandlers'],
  getNextTreeSelectionCandidate: (selection?: string[]) => string | null,
  navigatorInterface: NavigatorViewInterface
) => {
  const multiselectIds = getMultiselectForContextMenu(
    node,
    selectedComponentIds
  );
  const selectedComponentId = [node.id];
  const componentIds = multiselectIds || selectedComponentId || [];

  eventHandlers.onComponentContextMenu?.({
    event,
    componentIds,
    isViewpointMode,
    targetComponentId: node.id,
    getNextTreeSelectionCandidate,
    navigatorInterface,
    currentSelectedComponentIds: selectedComponentIds,
  });
};

const createContextMenuForWorkspace = (
  event: MouseEvent,
  node: NodeModel,
  isViewpointMode: boolean,
  scenarioId: string | null,
  eventHandlers: NavigatorConfiguration['eventHandlers']
) =>
  eventHandlers.onWorkspaceContextmenu?.(
    event,
    [node.id],
    scenarioId,
    isViewpointMode
  );

const getMultiselectForContextMenu = (
  targetNode: NodeModel | null,
  selection: string[]
) => {
  if (selection.length === 0) {
    return null;
  }
  const actionInSelection =
    targetNode && selection.some(id => id === targetNode.id);
  return actionInSelection ? selection : null;
};

const createContextMenuForScenario = (
  event: MouseEvent,
  scenarioId: string | null,
  eventHandlers: NavigatorConfiguration['eventHandlers']
) => eventHandlers.onScenarioContextmenu(event, scenarioId);
